// ── Neon status/stage color system — single source of truth ──
// Stephen's "wildly colorful" admin redesign. Every status maps to a solid
// badge color, readable badge text, a faint row tint, and an accent stripe so
// each status is instantly distinguishable on every admin page.
//
// Keep status ids in sync with the workflow config
// (pending / sculpting / feedback_needed / changes_requested / approved / shipped / in_production).
// Labels still come from utils/labelMapper.js — this module owns COLOR only.

export const STATUS_NEON = {
  pending:           { bg: '#2f6bff', text: '#ffffff', tint: '#e6ecff', accent: '#2f6bff' },
  sculpting:         { bg: '#0aa2ff', text: '#ffffff', tint: '#dcf1ff', accent: '#0aa2ff' },
  feedback_needed:   { bg: '#ffab00', text: '#3a2600', tint: '#fff2d6', accent: '#ffab00' },
  changes_requested: { bg: '#ff4d2e', text: '#ffffff', tint: '#ffe1da', accent: '#ff4d2e' },
  approved:          { bg: '#11d97a', text: '#053a22', tint: '#d6fdec', accent: '#11d97a' },
  shipped:           { bg: '#7b3ff2', text: '#ffffff', tint: '#ece1ff', accent: '#7b3ff2' },
  in_production:     { bg: '#0aa2ff', text: '#ffffff', tint: '#dcf1ff', accent: '#0aa2ff' },
};

export const NEON_FALLBACK = { bg: '#64748b', text: '#ffffff', tint: '#eef1f6', accent: '#64748b' };

export const getStatusColors = (statusId) => STATUS_NEON[statusId] || NEON_FALLBACK;

// Bright stage pill hues — clay/paint/shipped keep their established identity, brightened.
export const STAGE_NEON = {
  clay: '#ff9e2c',
  paint: '#3b82ff',
  shipped: '#11d97a',
  fulfilled: '#11d97a',
  canceled: '#ff4d2e',
};
export const getStageColor = (stageId) => STAGE_NEON[stageId] || '#7b3ff2';

// Inline style for a solid neon status badge. `glow` adds a colored halo.
export const statusBadgeStyle = (statusId, { glow = false } = {}) => {
  const c = getStatusColors(statusId);
  return {
    backgroundColor: c.bg,
    color: c.text,
    boxShadow: glow ? `0 0 0 1px ${c.bg}33, 0 2px 10px ${c.bg}66` : undefined,
  };
};

// Inline style for a solid neon stage badge.
export const stageBadgeStyle = (stageId) => ({
  backgroundColor: getStageColor(stageId),
  color: '#ffffff',
});

// ── Shared neon palette ──
// Named accents reused by admin surfaces (Settings sections, cards, headers) so
// the whole admin draws from ONE palette. Hues mirror the status/stage colors above.
export const NEON = {
  blue:   '#2f6bff',  // primary / branding
  cyan:   '#0aa2ff',  // workflow
  purple: '#7b3ff2',  // integrations
  amber:  '#ffab00',  // email
  green:  '#11d97a',  // users / success / connected
  red:    '#ff4d2e',  // danger / disconnected
  slate:  '#64748b',  // neutral / disabled
};

// Style helpers for cohesive neon section cards.
// Left accent stripe + faint tinted header, keyed by a NEON hue.
export const sectionCardStyle = (hue) => ({
  borderTop: `3px solid ${hue}`,
  boxShadow: `0 1px 3px ${hue}22, 0 8px 24px ${hue}14`,
});

export const sectionIconStyle = (hue) => ({
  backgroundColor: `${hue}1a`,
  color: hue,
});
